import { Fragment, type ReactNode } from "react";
import { richBlocksFromHtml, type RichRun } from "@/lib/rich-line-text";
import { cn } from "@/lib/utils";

/**
 * A quote line's words with their bold / italic / bullets / numbers
 * (Faster Estimates, decision 5). The stored copy is parsed into blocks and
 * drawn as React elements — never innerHTML — so even a copy that somehow
 * skipped the server's sanitizer can't inject markup. No rich copy: the plain
 * words, exactly as before.
 */
export function RichLineText({
    html,
    text,
    className,
}: {
    html?: string | null;
    text: string | null | undefined;
    className?: string;
}) {
    const blocks = html ? richBlocksFromHtml(html) : [];
    if (blocks.length === 0) return <span className={className}>{text || "—"}</span>;
    return (
        <div className={cn("flex flex-col gap-1", className)} data-testid="rich-line-text">
            {blocks.map((b, k) =>
                b.type === "p" ? (
                    <p key={k} className="m-0">
                        {runs(b.runs)}
                    </p>
                ) : b.type === "ul" ? (
                    <ul key={k} className="m-0 list-disc pl-5">
                        {b.items.map((it, j) => (
                            <li key={j}>{runs(it)}</li>
                        ))}
                    </ul>
                ) : (
                    <ol key={k} className="m-0 list-decimal pl-5">
                        {b.items.map((it, j) => (
                            <li key={j}>{runs(it)}</li>
                        ))}
                    </ol>
                ),
            )}
        </div>
    );
}

function runs(list: RichRun[]): ReactNode {
    return list.map((r, k) => {
        if (r.br) return <br key={k} />;
        let node: ReactNode = r.text;
        if (r.i) node = <em>{node}</em>;
        if (r.b) node = <strong className="font-semibold">{node}</strong>;
        return <Fragment key={k}>{node}</Fragment>;
    });
}
